import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { CaretLeftIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  /** Small-caps section label above the title (e.g. "Κριτικές"). */
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Back link (detail pages). */
  back?: { to: string; label: ReactNode };
  /** Inline meta under the subtitle (badges, counts). */
  meta?: ReactNode;
  /** Optional tabs/filters row rendered under the title block. */
  children?: ReactNode;
  className?: string;
}

/**
 * Page header (A3): blue uppercase eyebrow, heavy Commissioner title (800, tight, 38px on desktop),
 * muted standfirst, actions on the right. No rule underneath: the white cards below carry the
 * structure. Emphasis inside a title is colour (`text-primary-ink`), never italics.
 */
export function PageHeader({
  title,
  eyebrow,
  subtitle,
  actions,
  back,
  meta,
  children,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('mb-7 animate-rise', className)}>
      {back && (
        <Link
          to={back.to}
          className="mb-3 inline-flex items-center gap-1 rounded-[3px] text-sm font-semibold text-ink-muted transition-colors hover:text-primary-ink focus-visible:outline-2 focus-visible:outline-focus"
        >
          <CaretLeftIcon className="size-3.5" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0 max-w-3xl">
          {eyebrow && <div className="eyebrow mb-1.5 text-primary-ink">{eyebrow}</div>}
          <h1 className="display-heavy text-[2rem] text-ink sm:text-3xl">{title}</h1>
          {subtitle && (
            <p className="mt-2 max-w-[62ch] text-[0.9375rem] leading-relaxed text-ink-muted">
              {subtitle}
            </p>
          )}
          {meta && <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </header>
  );
}
